/**
 * Sentry Error Tracking - Client-Side Initialization
 *
 * Initializes Sentry for the Vue frontend with:
 * - Vue component error tracking
 * - Router navigation breadcrumbs
 * - Performance monitoring (traces)
 * - User context (set after login)
 * - Environment-aware configuration
 *
 * Usage:
 *   import { initSentry, setSentryUser, clearSentryUser, captureError } from './sentry';
 *   initSentry(app, router);
 */
import * as Sentry from '@sentry/vue';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || '';
const isProd = import.meta.env.PROD;
const isEnabled = import.meta.env.VITE_SENTRY_ENABLED === 'true' && !!SENTRY_DSN;

/**
 * Initialize Sentry. Call once before app.mount().
 * @param {import('vue').App} app - The Vue app instance
 * @param {import('vue-router').Router} router - The Vue Router instance
 */
export function initSentry(app, router) {
  if (!isEnabled) {
    console.info('[Sentry] Disabled — VITE_SENTRY_DSN not set');
    return;
  }

  Sentry.init({
    app,
    dsn: SENTRY_DSN,
    environment: isProd ? 'production' : 'development',
    release: import.meta.env.VITE_APP_VERSION || 'eventflow@1.0.0',

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        // Only capture replays on errors (not all sessions)
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Performance monitoring — sample 10% of production traffic
    tracesSampleRate: isProd ? 0.1 : 1.0,

    // Session replay — capture 0% of sessions, 100% of sessions with errors
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: isProd ? 1.0 : 0,

    // Filter out noisy / irrelevant errors
    beforeSend(event, hint) {
      const error = hint?.originalException;

      // Ignore network errors that are likely transient
      if (error?.message?.match(/Failed to fetch|NetworkError|Load failed/i)) {
        return null;
      }

      // Ignore ResizeObserver errors (browser noise)
      if (error?.message?.match(/ResizeObserver/i)) {
        return null;
      }

      return event;
    },

    // Breadcrumb filtering — reduce noise
    beforeBreadcrumb(breadcrumb) {
      // Filter out noisy console.log breadcrumbs in production
      if (isProd && breadcrumb.category === 'console' && breadcrumb.level === 'log') {
        return null;
      }
      return breadcrumb;
    },

    // Limit data to Supabase domain and own domain
    tracePropagationTargets: [
      /^https:\/\/.*\.supabase\.co/,
      /^https:\/\/.*event-flow\.co/,
      'localhost',
    ],
  });

  console.info('[Sentry] Initialized for', isProd ? 'production' : 'development');
}

/**
 * Set user context after authentication.
 * @param {{ id: string, email: string, organizationId?: string, role?: string }} userInfo
 */
export function setSentryUser(userInfo) {
  if (!isEnabled) return;

  Sentry.setUser({
    id: userInfo.id,
    email: userInfo.email,
  });

  if (userInfo.organizationId) {
    Sentry.setTag('organization_id', userInfo.organizationId);
  }
  if (userInfo.role) {
    Sentry.setTag('user_role', userInfo.role);
  }
}

/**
 * Clear user context on logout.
 */
export function clearSentryUser() {
  if (!isEnabled) return;
  Sentry.setUser(null);
}

/**
 * Manually capture an error with optional context.
 * Use this in catch blocks for important operations.
 *
 * @param {Error} error - The error to capture
 * @param {Record<string, any>} [context] - Additional context
 */
export function captureError(error, context) {
  if (!isEnabled) {
    console.error('[Sentry disabled]', error, context);
    return;
  }

  if (context) {
    Sentry.withScope((scope) => {
      for (const [key, value] of Object.entries(context)) {
        scope.setExtra(key, value);
      }
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
}

/**
 * Capture a custom message (non-error) for alerting or tracking.
 * @param {string} message - The message
 * @param {'info' | 'warning' | 'error'} [level='info'] - Severity level
 * @param {Record<string, any>} [context] - Additional context
 */
export function captureMessage(message, level = 'info', context) {
  if (!isEnabled) {
    console.info('[Sentry disabled]', message, context);
    return;
  }

  Sentry.withScope((scope) => {
    if (context) {
      for (const [key, value] of Object.entries(context)) {
        scope.setExtra(key, value);
      }
    }
    Sentry.captureMessage(message, level);
  });
}
